// RITA-1498 (Nutzungserfassung Phase 1b) — Änderungen der Darstellungseinstellungen (Konzept 3.6).
// Diff des Einstellungs-Objekts statt Einhängen in die Änderungsfunktionen: SettingsContext
// spielt beim Start alle Vorgaben erneut ein (useEffect in SettingsContext.tsx); solche
// Änderungen vor der ersten Bedienhandlung sind Initialisierung und werden nicht gemeldet.
import { useEffect, useRef } from 'react';
import type { SnapshotSettings } from '../services/deviceInfo';
import type { InteractionHints } from '../services/navigation';
import type { UsageTracker } from '../services/tracker';

// Namen entsprechen dem Server-Enum für setting-Ereignisse (batchSchema.ts).
export const TRACKED_SETTING_KEYS = [
  'themeMode',
  'themeLayout',
  'themeContrast',
  'themeColorPresets',
  'themeStretch',
] as const;

export interface SettingsUsageDeps {
  tracker: UsageTracker;
  hints: InteractionHints;
  active: boolean;
  settings: SnapshotSettings;
}

const pick = (settings: SnapshotSettings): SnapshotSettings => ({
  themeMode: settings.themeMode,
  themeLayout: settings.themeLayout,
  themeContrast: settings.themeContrast,
  themeColorPresets: settings.themeColorPresets,
  themeStretch: settings.themeStretch,
});

export const useSettingsUsage = ({ tracker, hints, active, settings }: SettingsUsageDeps): void => {
  const previousRef = useRef<SnapshotSettings | null>(null);
  const { themeMode, themeLayout, themeContrast, themeColorPresets, themeStretch } = settings;

  useEffect(() => {
    if (!active) {
      previousRef.current = null;
      return;
    }
    const before = previousRef.current;
    const current = pick({ themeMode, themeLayout, themeContrast, themeColorPresets, themeStretch });
    previousRef.current = current;
    if (!before || !hints.hasInteracted()) return;
    TRACKED_SETTING_KEYS.forEach((key) => {
      if (before[key] !== current[key]) {
        tracker.record({
          type: 'setting',
          name: key,
          props: { from: String(before[key]), to: String(current[key]) },
        });
      }
    });
  }, [active, tracker, hints, themeMode, themeLayout, themeContrast, themeColorPresets, themeStretch]);
};

export default useSettingsUsage;
